import type { Course, AppSettings, CourseContribution } from '@gpa-scales/shared-types';
import { AGSubject, GradeLevel, CourseType } from '@gpa-scales/shared-types';
import { resolveGradePoints } from './gradeConversion';
import { isHonorsEligible } from './honorsEligibility';

export function computeContributions(
  courses: Course[],
  settings: AppSettings
): CourseContribution[] {
  return courses.map((course) => {
    let gradePoints: number;
    let hasValidGrade: boolean;
    try {
      const resolved = resolveGradePoints(course, settings);
      gradePoints = resolved ?? 0;
      hasValidGrade = resolved !== null;
    } catch {
      gradePoints = 0;
      hasValidGrade = false;
    }
    const isUCEligible =
      course.agSubject !== AGSubject.None &&
      (course.gradeLevel === GradeLevel.Sophomore || course.gradeLevel === GradeLevel.Junior);
    const isAdvanced = [
      CourseType.AP,
      CourseType.IB_HL,
      CourseType.IB_SL,
      CourseType.Honors,
      CourseType.DualEnrollment,
    ].includes(course.courseType);
    const bonus = isAdvanced && hasValidGrade && isHonorsEligible(course, settings) ? 1.0 : 0;

    const includedIn = {
      unweighted: hasValidGrade,
      ucCapped: hasValidGrade && isUCEligible,
      ucUncapped: hasValidGrade && isUCEligible,
      ucUnweighted: hasValidGrade && isUCEligible,
      commonApp: hasValidGrade,
    };

    const exclusionReasons: CourseContribution['exclusionReasons'] = {};
    if (!hasValidGrade) {
      exclusionReasons.unweighted = 'No valid grade';
      exclusionReasons.ucCapped = 'No valid grade';
      exclusionReasons.ucUncapped = 'No valid grade';
      exclusionReasons.ucUnweighted = 'No valid grade';
      exclusionReasons.commonApp = 'No valid grade';
    } else {
      if (!isUCEligible) {
        const reasons: string[] = [];
        if (course.agSubject === AGSubject.None) reasons.push('Not an A-G course');
        if (course.gradeLevel !== GradeLevel.Sophomore && course.gradeLevel !== GradeLevel.Junior) {
          reasons.push(`Grade ${course.gradeLevel} (UC requires 10th–11th)`);
        }
        const reason = reasons.join('; ');
        exclusionReasons.ucCapped = reason;
        exclusionReasons.ucUncapped = reason;
        exclusionReasons.ucUnweighted = reason;
      }
    }

    const explanationParts: string[] = [];
    if (!hasValidGrade) {
      explanationParts.push('Excluded: no valid grade');
    } else {
      explanationParts.push(`Grade points: ${gradePoints.toFixed(1)}`);
      if (bonus > 0) {
        explanationParts.push(`Honors bonus: +${bonus.toFixed(1)}`);
      }
      if (course.agSubject !== AGSubject.None) {
        explanationParts.push(`A-G: ${course.agSubject}`);
      }
      if (isUCEligible) {
        explanationParts.push('Included in UC calculations');
      } else {
        explanationParts.push('Excluded from UC (not A-G or outside 10th–11th)');
      }
    }

    return {
      courseId: course.id,
      gradePoints,
      bonusPoints: bonus,
      totalPoints: gradePoints + bonus,
      credits: course.credits,
      includedIn,
      exclusionReasons,
      explanation: explanationParts.join('. ') + '.',
    };
  });
}
